import React, { useState } from 'react'
import { Typography, Stack } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import ResponsiveModalDrawerDialog from 'src/Elements/Utilities/ResponsiveModalDrawerDialog'
import { ActionProps } from 'src/Elements/Utilities/ResponsiveModalDrawerActions'
import OwnerAccessRequestDialogTable from './OwnerAccessRequestDialogTable'
import OwnerAccessRequestIgnoreButton from './OwnerAccessRequestIgnoreButton'
import OwnerAccessRequestAccordion from './OwnerAccessRequestAccordion'
import { updateAccessRequest } from '../../api'
import { OwnerAccessRequestModel, OwnerAccessRequestUpdateActions } from '../../models'
import { useDialogSession } from '../../hooks'
import { getOwnerAccessRequestSessionKey } from '../../utils'

export interface OwnerAccessRequestDialogProps {
  onClose?: () => void
  place?: number
  installationId: number
  pendingAccessRequests: OwnerAccessRequestModel[]
}

/**
 * Dialog component to accept/reject/ignore access request.
 * It can be used to pop-up multiple dialogs, one at a time.
 * after user clicks 'accept/reject/ignore/x', next dialog is opened.
 * currentIndex keep track of the current dialog.
 * once all the dialogs are shown, a flag is stored in the session storage using markDialogAsShown.
 * the flag will prevent the pop-ups from showing again in the current session.
 */
const OwnerAccessRequestDialog: React.FC<OwnerAccessRequestDialogProps> = ({ onClose, installationId, pendingAccessRequests, place }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const ownerAccessRequestSessionKey = getOwnerAccessRequestSessionKey(installationId)
  const { markDialogAsShown } = useDialogSession(ownerAccessRequestSessionKey)
  const [currentIndex, setCurrentIndex] = useState(0)

  const isLastIndex = currentIndex === pendingAccessRequests.length - 1
  const { id: accessRequestId, createdAt, companyName, reason } = pendingAccessRequests[currentIndex]

  /**
   * increment the current index to show the next dialog.
   * if it is the last dialog, store a flag in session storage using markDialogAsShown.
   * the flag will prevent the dialogs from popping up again in the current session.
   */
  const handleDialog = (): void => {
    if (!isLastIndex) {
      setCurrentIndex(prev => prev + 1)
      return
    }
    markDialogAsShown()
    onClose?.()
  }

  const handleOnAccept = async (): Promise<void> => {
    if (!installationId) return

    const { hasErrors } = await updateAccessRequest(OwnerAccessRequestUpdateActions.accept, installationId, accessRequestId, authFetch)

    if (hasErrors) {
      enqueueErrorStack(t('dashboard.access-request-dialog.button.accept-request-failure-message'))
      return
    }
    enqueueSuccessStack(t('dashboard.access-request-dialog.button.accept-request-success-message'))

    handleDialog()
  }

  const handleOnReject = async (): Promise<void> => {
    if (!installationId) return

    const { hasErrors } = await updateAccessRequest(OwnerAccessRequestUpdateActions.reject, installationId, accessRequestId, authFetch)

    if (hasErrors) {
      enqueueErrorStack(t('dashboard.access-request-dialog.button.reject-request-failure-message'))
      return
    }
    enqueueSuccessStack(t('dashboard.access-request-dialog.button.reject-request-success-message'))

    handleDialog()
  }

  const handleOnIgnore = (): void => {
    handleDialog()
  }

  const primaryAction: ActionProps = {
    label: t('dashboard.access-request-dialog.button.accept'),
    onClick: handleOnAccept,
  }

  const secondaryAction: ActionProps = {
    label: t('dashboard.access-request-dialog.button.reject'),
    onClick: handleOnReject,
    variant: 'contained',
    color: 'warning',
  }

  const ignoreButton = <OwnerAccessRequestIgnoreButton onClick={handleOnIgnore} />

  return (
    <ResponsiveModalDrawerDialog
      title={t('dashboard.access-request-dialog.title')}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      leftControls={ignoreButton}
      onClose={handleDialog}
      modalMaxWidth="sm"
    >
      <Stack gap={4}>
        <Typography variant="body2">{t('dashboard.access-request-dialog.text-description')}</Typography>
        <OwnerAccessRequestDialogTable installationId={installationId} accessRequestAt={createdAt} company={companyName} />
        <Typography variant="body1">{reason}</Typography>
        <OwnerAccessRequestAccordion />
      </Stack>
    </ResponsiveModalDrawerDialog>
  )
}

export default OwnerAccessRequestDialog
